import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Edit3,
  Loader2,
  Plus,
  RefreshCw,
  Store,
  Trash2,
  X,
  ChevronDown,
  ChevronRight,
  Search,
  Layers3,
  Image as ImageIcon,
  Tag,
  Clock3,
  CircleHelp,
} from "lucide-react";
import useBulkCatalogue from "../../services/Order/useBulkCatalogue";
import { BulkMenus, BulkMenuPackageMappings } from "./BulkPackages";
import {
  BulkPackageList,
  BulkPackageVariants,
  BulkPackageItemMappings,
} from "./BulkPackageManagement";
import BulkOptionGroups from "./BulkOptionGroups";
import BulkPackageOptionGroups from "./BulkPackageOptionGroups";
import BulkOptionGroupItems from "./BulkOptionGroupItems";
import {
  BulkPackageGroupSets,
  BulkPackageGroupSetMembers,
} from "./BulkPackageGroupSets";
import { BulkAddons, BulkPackageAddons } from "./BulkAddons";

const MANAGEMENT_TABS = [
  { id: "basic-setup", label: "1. Basic Setup" },
  { id: "menu-packages", label: "2. Menus & Packages" },
  { id: "items-pricing", label: "3. Items & Pricing" },
  { id: "package-contents", label: "4. Package Contents" },
  { id: "customization", label: "5. Package Customization" },
  { id: "addons-management", label: "6. Addons Management" },
];

const BULK_CATALOGUE_HELP = {
  // restaurant: {
  //   title: "Restaurant",
  //   path: "1. Basic Setup → Restaurant",
  //   summary: "Create and manage bulk food restaurants.",
  //   steps: [
  //     "Click Add Restaurant to create a restaurant.",
  //     "Enter the restaurant name, phone and status.",
  //     "Use Edit or Delete to manage existing restaurants.",
  //     "Next, create a Catalogue for the restaurant.",
  //   ],
  // },
  catalogue: {
    title: "Catalogue",
    path: "1. Basic Setup → Catalogue",
    summary: "Create a catalogue linked to a restaurant.",
    steps: [
      "Click Add Catalogue.",
      "Select the restaurant and enter the catalogue name.",
      "Set the validity dates and status, then save.",
    ],
  },
  "bulk-menu": {
    title: "Bulk Menu",
    path: "2. Menus & Packages → Bulk Menu",
    summary: "Create top-level menus for your packages.",
    steps: [
      "Click Add Menu to create a new menu.",
      "Enter its name, catalogue, icon and display order.",
      "Use Menu Package Mapping to link packages.",
    ],
  },
  packages: {
    title: "Packages",
    path: "2. Menus & Packages → Packages",
    summary: "Create food packages and configure their pricing.",
    steps: [
      "Click Add Package.",
      "Choose the catalogue, menu and package type.",
      "Set pricing method, person limits and base price.",
      "Leave Parent Package empty for an independent package.",
    ],
  },
  "package-variants": {
    title: "People & Price",
    path: "2. Menus & Packages → Package Variants",
    summary: "Add or edit People & Price options.",
    steps: [
      "Click Add Variant and select a package.",
      "Enter the variant name, such as 2 Persons.",
      "Set the person count or range and selling price.",
      "Save to add another People & Price option.",
      "Use Edit to update an existing price.",
    ],
  },
  "menu-package": {
    title: "Menu Package Mapping",
    path: "2. Menus & Packages → Menu Package Mapping",
    summary: "Link an existing package to a menu.",
    steps: [
      "Click Add Mapping.",
      "Choose the menu and package.",
      "Set display order and primary status.",
      "Save the mapping.",
    ],
  },
  category: {
    title: "Category",
    path: "3. Items & Pricing → Category",
    summary: "Create food categories and subcategories.",
    steps: [
      "Click Add Category.",
      "Choose the restaurant and enter a category name.",
      "Select a parent only when creating a subcategory.",
      "Set display order and save.",
    ],
  },
  item: {
    title: "Items & Pricing",
    path: "3. Items & Pricing → Items & Pricing",
    summary: "Manage items, variants, prices and price tiers.",
    steps: [
      "Click Add Item to create a food item.",
      "Expand the item and select Add Variant.",
      "Expand the variant and select Add Price.",
      "Expand the price and select Add Tier if needed.",
    ],
  },
  "item-category-mapping": {
    title: "Item Category Mapping",
    path: "3. Items & Pricing → Item Category Mapping",
    summary: "Assign existing food items to categories.",
    steps: [
      "Click Add Mapping.",
      "Select the item and category.",
      "Choose whether it is the primary category.",
      "Save the mapping.",
    ],
  },
  "package-items": {
    title: "Package Item Mapping",
    path: "4. Package Contents → Package Item Mapping",
    summary: "Add existing food items to a package.",
    steps: [
      "Create the package and items first.",
      "Click Add Mapping.",
      "Choose the package and food item.",
      "Save the relationship.",
    ],
  },
  "option-groups": {
    title: "Option Groups",
    path: "5. Package Customization → Option Groups",
    summary: "Create groups of customer choices.",
    steps: [
      "Create an option group, such as Choose Your Bread.",
      "Add available food choices through Option Group Items.",
      "Connect the group through Package Option Groups.",
    ],
  },
  "package-option-groups": {
    title: "Package Option Groups",
    path: "5. Package Customization → Package Option Groups",
    summary: "Define the selection rules for package choices.",
    steps: [
      "Choose a package and option group.",
      "Select the selection type and quantity rule.",
      "Set minimum and maximum selections.",
      "Save the package option group.",
    ],
  },
  "option-group-items": {
    title: "Option Group Items",
    path: "5. Package Customization → Option Group Items",
    summary: "Add food choices inside an option group.",
    steps: [
      "Select the option group and item.",
      "Configure its variant and default selection.",
      "Set quantity and extra price where applicable.",
      "Save the item option.",
    ],
  },
  "package-group-sets": {
    title: "Package Group Sets",
    path: "5. Package Customization → Package Group Sets",
    summary: "Organize multiple customization groups.",
    steps: [
      "Select a package and create a group set.",
      "Set minimum and maximum groups to select.",
      "Add groups using Group Set Members.",
    ],
  },
  "package-group-set-members": {
    title: "Group Set Members",
    path: "5. Package Customization → Group Set Members",
    summary: "Add customization groups to an existing set.",
    steps: [
      "Select the package group set.",
      "Choose a package option group.",
      "Save the group set member.",
    ],
  },
  addons: {
    title: "Addons",
    path: "6. Addons Management → Addons",
    summary: "Create additional paid food options.",
    steps: [
      "Click Add Addon to create an extra option.",
      "Enter its name, price and price type.",
      "Link an item or variant when applicable.",
      "Save the addon.",
    ],
  },
  "package-addons": {
    title: "Package Addons",
    path: "6. Addons Management → Package Addons",
    summary: "Make existing addons available for packages.",
    steps: [
      "Create the addon first.",
      "Choose the package and addon.",
      "Save the package-addon mapping.",
    ],
  },
};

function BulkCatalogueHelpGuide({ guide }) {
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const visible = hovered || expanded;

  return (
    <div
      className="relative z-30 shrink-0"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          setExpanded(false);
          setHovered(false);
        }
      }}
    >
      <button
        type="button"
        onClick={() => {
          setExpanded((prev) => !prev);
          if (expanded) setHovered(false);
        }}
        aria-expanded={visible}
        aria-label="Help guide for current tab"
        title="Hover for a quick tip; click for full instructions"
        className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition ${
          visible
            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
        }`}
      >
        <CircleHelp size={16} />
        <span className="hidden sm:inline">Help Guide</span>
      </button>

      {visible && (
        <div className="absolute right-0 top-full z-50 w-80 max-w-[calc(100vw-2rem)] pt-2 sm:w-96">
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
              <div>
                <p className="text-sm font-bold text-slate-900">
                  {guide.title}
                </p>
                <p className="mt-1 text-[11px] text-emerald-700">
                  {guide.path}
                </p>
              </div>

              {expanded && (
                <button
                  type="button"
                  onClick={() => {
                    setExpanded(false);
                    setHovered(false);
                  }}
                  aria-label="Close Help Guide"
                  className="rounded-md p-1 hover:bg-slate-100"
                >
                  <X size={15} />
                </button>
              )}
            </div>

            <div className="px-4 py-3">
              <p className="text-xs leading-5 text-slate-700">
                {guide.summary}
              </p>

              {expanded ? (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <p className="mb-2 text-xs font-bold">How to use this tab</p>

                  <ol className="space-y-2">
                    {guide.steps.map((step, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-[10px] font-bold text-emerald-700">
                          {index + 1}
                        </span>

                        <span className="text-xs leading-5 text-slate-700">
                          {step}
                        </span>
                      </li>
                    ))}
                  </ol>

                  <p className="mt-4 border-t border-slate-100 pt-3 text-[11px] text-slate-500">
                    + Add: create · Pencil: edit · Trash: delete · Refresh:
                    reload
                  </p>
                </div>
              ) : (
                <p className="mt-2 text-[11px] text-emerald-700">
                  Click Help Guide for detailed instructions.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const FIXED_WORKSPACE_TAB_CLASS = [
  // Workspace scrollable rahega, scrollbar hidden
  "min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pb-12",
  "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",

  // Sub-tabs fixed
  "[&>div:first-child]:sticky",
  "[&>div:first-child]:top-0",
  "[&>div:first-child]:z-30",
  "[&>div:first-child]:bg-white",

  // Cover the entire 12px gap below sub-tabs
  "[&>div:first-child]:shadow-[0_12px_0_0_#f8fafc]",
  // Direct section header fixed
  "[&>section>div:first-child]:sticky",
  "[&>section>div:first-child]:top-[62px]",
  "[&>section>div:first-child]:z-20",

  // Mask the gap above header
  "[&>section>div:first-child]:before:content-['']",
  "[&>section>div:first-child]:before:absolute",
  "[&>section>div:first-child]:before:-top-3",
  "[&>section>div:first-child]:before:inset-x-0",
  "[&>section>div:first-child]:before:h-3",
  "[&>section>div:first-child]:before:bg-slate-50",
  "[&>section>div:first-child]:before:pointer-events-none",

  // Wrapped section headers fixed
  "[&>div:nth-child(2)>div:first-child]:sticky",
  "[&>div:nth-child(2)>div:first-child]:top-[62px]",
  "[&>div:nth-child(2)>div:first-child]:z-20",

  // Mask gap above wrapped headers
  "[&>div:nth-child(2)>div:first-child]:before:content-['']",
  "[&>div:nth-child(2)>div:first-child]:before:absolute",
  "[&>div:nth-child(2)>div:first-child]:before:-top-3",
  "[&>div:nth-child(2)>div:first-child]:before:inset-x-0",
  "[&>div:nth-child(2)>div:first-child]:before:h-3",
  "[&>div:nth-child(2)>div:first-child]:before:bg-slate-50",
  "[&>div:nth-child(2)>div:first-child]:before:pointer-events-none",

  // Items & Pricing extra wrapper
  "[&>div:nth-child(2)>section>div:first-child]:sticky",
  "[&>div:nth-child(2)>section>div:first-child]:top-[62px]",
  "[&>div:nth-child(2)>section>div:first-child]:z-20",

  // Mask gap above Items & Pricing header
  "[&>div:nth-child(2)>section>div:first-child]:before:content-['']",
  "[&>div:nth-child(2)>section>div:first-child]:before:absolute",
  "[&>div:nth-child(2)>section>div:first-child]:before:-top-3",
  "[&>div:nth-child(2)>section>div:first-child]:before:inset-x-0",
  "[&>div:nth-child(2)>section>div:first-child]:before:h-3",
  "[&>div:nth-child(2)>section>div:first-child]:before:bg-slate-50",
  "[&>div:nth-child(2)>section>div:first-child]:before:pointer-events-none",
].join(" ");



export default function BulkRestaurants() {
  const [activeManagementTab, setActiveManagementTab] = useState("basic-setup");

  const [activeSetupTab, setActiveSetupTab] = useState("catalogue");

  const [activeMenuPackageTab, setActiveMenuPackageTab] = useState("bulk-menu");

  // Step 3 - Items & Pricing
  const [activeItemsPricingTab, setActiveItemsPricingTab] =
    useState("category");

  // Step 4 - Package Contents
  const [activePackageContentsTab, setActivePackageContentsTab] =
    useState("package-items");

  // Step 5 - Package Customization
  const [activeCustomizationTab, setActiveCustomizationTab] =
    useState("option-groups");

  // Step 6 - Addons Management
  const [activeAddonsTab, setActiveAddonsTab] = useState("addons");
  const workspaceRef = useRef(null);
  const [workspaceHeight, setWorkspaceHeight] = useState(0);

  useEffect(() => {
    const updateWorkspaceHeight = () => {
      if (!workspaceRef.current) return;

      const top = workspaceRef.current.getBoundingClientRect().top;
      const availableHeight = window.innerHeight - top - 16;

      setWorkspaceHeight(Math.max(160, availableHeight));
    };

    updateWorkspaceHeight();

    window.addEventListener("resize", updateWorkspaceHeight);

    return () => {
      window.removeEventListener("resize", updateWorkspaceHeight);
    };
  }, []);

  // Detect the currently selected sub-tab
  const selectedHelpTab = {
    // "basic-setup": activeSetupTab,
    "menu-packages": activeMenuPackageTab,
    "items-pricing": activeItemsPricingTab,
    "package-contents": activePackageContentsTab,
    customization: activeCustomizationTab,
    "addons-management": activeAddonsTab,
  }[activeManagementTab];

  // Display the correct instructions
  const activeGuide =
    BULK_CATALOGUE_HELP[selectedHelpTab] || BULK_CATALOGUE_HELP.restaurant;

return (
  <div
    ref={workspaceRef}
    style={{
      height: workspaceHeight ? `${workspaceHeight}px` : "calc(100dvh - 180px)",
    }}
    className="flex min-h-0 w-full max-w-8xl flex-col gap-3 overflow-hidden"
  >
    <div className="relative z-40 flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white p-1.5 shadow-sm">
      {/* Existing Main Navigation */}
      <div className="min-w-0 flex-1 overflow-x-auto">
        <div className="flex min-w-max items-center gap-1">
          {MANAGEMENT_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveManagementTab(tab.id)}
              className={`h-9 rounded-lg px-4 text-xs font-semibold transition ${
                activeManagementTab === tab.id
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* New Context-Sensitive Help Guide */}
      <BulkCatalogueHelpGuide guide={activeGuide} />
    </div>

    {activeManagementTab === "basic-setup" && (
      <div className={FIXED_WORKSPACE_TAB_CLASS}>
        {/* Basic Setup Sub Tabs */}
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
          {/* <button
            type="button"
            onClick={() => setActiveSetupTab("restaurant")}
            className={`rounded-lg px-4 py-2 text-xs font-semibold transition ${
              activeSetupTab === "restaurant"
                ? "bg-emerald-600 text-white shadow-sm"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            1. Restaurant
          </button> */}

          <button
            type="button"
            onClick={() => setActiveSetupTab("catalogue")}
            className={`rounded-lg px-4 py-2 text-xs font-semibold transition ${
              activeSetupTab === "catalogue"
                ? "bg-emerald-600 text-white shadow-sm"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Catalogue
          </button>
        </div>

        {/* Existing Components */}
        {/* {activeSetupTab === "restaurant" && <BulkRestaurantsContent />} */}

        {activeSetupTab === "catalogue" && <BulkCatalogues />}
      </div>
    )}

    {activeManagementTab === "items-pricing" && (
      <div className={FIXED_WORKSPACE_TAB_CLASS}>
        {/* Step 3 Sub Tabs */}
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
          <div className="flex min-w-max items-center gap-2">
            {[
              { id: "category", label: "1. Category" },
              { id: "item", label: "2. Items & Pricing" },
              {
                id: "item-category-mapping",
                label: "3. Item Category Mapping",
              },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveItemsPricingTab(tab.id)}
                className={`rounded-lg px-4 py-2 text-xs font-semibold transition ${
                  activeItemsPricingTab === tab.id
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Existing Components - No Internal Changes */}

        {activeItemsPricingTab === "category" && <BulkItemCategories />}

        {activeItemsPricingTab === "item" && <BulkItemManagement />}

        {activeItemsPricingTab === "item-category-mapping" && (
          <BulkItemCategoryMappings />
        )}
      </div>
    )}

    {activeManagementTab === "menu-packages" && (
      <div className={FIXED_WORKSPACE_TAB_CLASS}>
        {/* Step 2 Sub Tabs */}
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
          <div className="flex min-w-max items-center gap-2">
            {[
              { id: "bulk-menu", label: "1. Bulk Menu" },
              { id: "packages", label: "2. Packages" },
              { id: "package-variants", label: "3. Package Variants" },
              {
                id: "menu-package",
                label: "4. Menu Package Mapping",
              },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveMenuPackageTab(tab.id)}
                className={`rounded-lg px-4 py-2 text-xs font-semibold transition ${
                  activeMenuPackageTab === tab.id
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Existing Components - Unchanged */}
        {activeMenuPackageTab === "bulk-menu" && <BulkMenus />}

        {activeMenuPackageTab === "packages" && <BulkPackageList />}

        {activeMenuPackageTab === "package-variants" && <BulkPackageVariants />}

        {activeMenuPackageTab === "menu-package" && <BulkMenuPackageMappings />}
      </div>
    )}

    {activeManagementTab === "package-contents" && (
      <div className={FIXED_WORKSPACE_TAB_CLASS}>
        {/* Step 4 - Package Contents Sub Tabs */}
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
          <div className="flex min-w-max items-center gap-2">
            <button
              type="button"
              onClick={() => setActivePackageContentsTab("package-items")}
              className={`rounded-lg px-4 py-2 text-xs font-semibold transition ${
                activePackageContentsTab === "package-items"
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              1. Package Item Mapping
            </button>
          </div>
        </div>

        {/* Existing Component */}
        {activePackageContentsTab === "package-items" && (
          <BulkPackageItemMappings />
        )}
      </div>
    )}

    {activeManagementTab === "customization" && (
      <div className={FIXED_WORKSPACE_TAB_CLASS}>
        {/* Step 5 - Package Customization Sub Tabs */}
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
          <div className="flex min-w-max items-center gap-2">
            {[
              {
                id: "option-groups",
                label: "1. Option Groups",
              },
              {
                id: "package-option-groups",
                label: "2. Package Option Groups",
              },
              {
                id: "option-group-items",
                label: "3. Option Group Items",
              },
              {
                id: "package-group-sets",
                label: "4. Package Group Sets",
              },
              {
                id: "package-group-set-members",
                label: "5. Group Set Members",
              },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveCustomizationTab(tab.id)}
                aria-pressed={activeCustomizationTab === tab.id}
                className={`rounded-lg px-4 py-2 text-xs font-semibold transition ${
                  activeCustomizationTab === tab.id
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Existing Customization Components */}

        {activeCustomizationTab === "option-groups" && <BulkOptionGroups />}

        {activeCustomizationTab === "package-option-groups" && (
          <BulkPackageOptionGroups />
        )}

        {activeCustomizationTab === "option-group-items" && (
          <BulkOptionGroupItems />
        )}

        {activeCustomizationTab === "package-group-sets" && (
          <BulkPackageGroupSets />
        )}

        {activeCustomizationTab === "package-group-set-members" && (
          <BulkPackageGroupSetMembers />
        )}
      </div>
    )}

    {activeManagementTab === "addons-management" && (
      <div className={FIXED_WORKSPACE_TAB_CLASS}>
        {/* Step 6 - Addons Management Sub Tabs */}
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
          <div className="flex min-w-max items-center gap-2">
            {[
              {
                id: "addons",
                label: "1. Addons",
              },
              {
                id: "package-addons",
                label: "2. Package Addons",
              },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveAddonsTab(tab.id)}
                aria-pressed={activeAddonsTab === tab.id}
                className={`rounded-lg px-4 py-2 text-xs font-semibold transition ${
                  activeAddonsTab === tab.id
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Existing Addons Components */}

        {activeAddonsTab === "addons" && <BulkAddons />}

        {activeAddonsTab === "package-addons" && <BulkPackageAddons />}
      </div>
    )}
  </div>
);
}

const EMPTY_FORM = {
  restaurant_id: "",
  restaurant_name: "",
  phone: "",
  status: "ACTIVE",
};

function BulkRestaurantsContent() {
  const {
    restaurantSaving,
    restaurantsLoading,
    restaurantDeletingId,
    getRestaurants,
    insertRestaurant,
    updateRestaurant,
    deleteRestaurant,
  } = useBulkCatalogue();
  const [restaurants, setRestaurants] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);


  const loadRestaurants = useCallback(async () => {
    const rows = await getRestaurants("");
    setRestaurants(Array.isArray(rows) ? rows : []);
  }, [getRestaurants]);

  useEffect(() => {
    loadRestaurants();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const openAdd = () => {
    setEditing({ mode: "ADD" });
    setForm(EMPTY_FORM);
  };
  const openEdit = (restaurant) => {
    setEditing({ mode: "EDIT" });
    setForm({
      restaurant_id: String(restaurant?.restaurant_id ?? restaurant?.id ?? ""),
      restaurant_name: String(restaurant?.restaurant_name ?? ""),
      phone: String(restaurant?.phone ?? ""),
      status: String(restaurant?.status ?? "ACTIVE"),
    });
  };
  const closeModal = () => {
    if (!restaurantSaving) {
      setEditing(null);
      setForm(EMPTY_FORM);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success =
      editing?.mode === "EDIT"
        ? await updateRestaurant(form)
        : await insertRestaurant(form);
    if (success) {
      closeModal();
      await loadRestaurants();
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.restaurant_id ?? deleteTarget?.id;
    if (!id) return;
    const success = await deleteRestaurant(id);
    if (success) {
      setDeleteTarget(null);
      await loadRestaurants();
    }
  };

  return (
    <div className="w-full max-w-8xl space-y-3">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
            <Store size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">
              Bulk Restaurants
            </h3>
            <p className="text-[11px] text-slate-500">
              Add, edit and delete Bulk Catalogue restaurants.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadRestaurants}
            disabled={restaurantsLoading}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw
              size={13}
              className={restaurantsLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700"
          >
            <Plus size={13} />
            Add Restaurant
          </button>
        </div>
      </div>

      {restaurantsLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-xl border border-slate-200 bg-white"
            />
          ))}
        </div>
      ) : restaurants.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-14 text-center">
          <Store size={26} className="mx-auto text-slate-300" />
          <p className="mt-2 text-sm font-semibold text-slate-800">
            No restaurants found
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Add your first Bulk Catalogue restaurant.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {restaurants.map((restaurant, index) => {
            const id = restaurant?.restaurant_id ?? restaurant?.id ?? index;
            const active =
              String(restaurant?.status || "").toUpperCase() === "ACTIVE";
            return (
              <div
                key={id}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500">
                  <Store size={15} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {restaurant?.restaurant_name || "Unnamed restaurant"}
                    </p>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
                    >
                      {restaurant?.status || "-"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    ID #{id} · {restaurant?.phone || "No phone"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(restaurant)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600"
                  title="Edit"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(restaurant)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 hover:bg-rose-100"
                  title="Delete"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing ? (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {editing.mode === "EDIT"
                    ? "Edit Restaurant"
                    : "Add Restaurant"}
                </h3>
                <p className="text-xs text-slate-500">
                  Bulk Catalogue restaurant details
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={restaurantSaving}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Restaurant Name
                  </span>
                  <input
                    name="restaurant_name"
                    value={form.restaurant_name}
                    onChange={handleChange}
                    disabled={restaurantSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Phone
                  </span>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    disabled={restaurantSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Status
                  </span>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    disabled={restaurantSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={restaurantSaving}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={restaurantSaving}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {restaurantSaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : editing.mode === "EDIT" ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}
                  {restaurantSaving
                    ? "Saving..."
                    : editing.mode === "EDIT"
                      ? "Update Restaurant"
                      : "Add Restaurant"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                <Trash2 size={17} />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Delete Restaurant
                </h3>
                <p className="mt-1 text-xs leading-5 text-slate-500">
                  Delete{" "}
                  <b>{deleteTarget?.restaurant_name || "this restaurant"}</b>?
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={Boolean(restaurantDeletingId)}
                className="h-9 rounded-lg border border-slate-200 px-4 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={Boolean(restaurantDeletingId)}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-rose-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              >
                {restaurantDeletingId ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const EMPTY_CATALOGUE_FORM = {
  catalogue_id: "",
  restaurant_id: "",
  catalogue_name: "",
  description: "",
  valid_from: "",
  valid_to: "",
  status: "ACTIVE",
};

function BulkCatalogues() {
  const {
    getRestaurants,
    getCatalogues,
    insertCatalogue,
    updateCatalogue,
    deleteCatalogue,
    catalogueSaving,
    cataloguesLoading,
    catalogueDeletingId,
  } = useBulkCatalogue();

  const [restaurants, setRestaurants] = useState([]);
  const [catalogues, setCatalogues] = useState([]);
  const [form, setForm] = useState(EMPTY_CATALOGUE_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const ssoUsername = localStorage.getItem("anlook_sso_username") || "";

  const normalizeName = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/marketting/g, "marketing");

  const getFixedRestaurant = (rows) =>
    rows.find(
      (r) => normalizeName(r?.restaurant_name) === normalizeName(ssoUsername),
    );

  const loadCatalogues = useCallback(async () => {
    const catalogueRows = await getCatalogues("");

    setCatalogues(Array.isArray(catalogueRows) ? catalogueRows : []);
  }, [getCatalogues]);

  useEffect(() => {
    loadCatalogues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureRestaurants = async () => {
    // Already loaded, avoid duplicate API call.
    if (restaurants.length > 0) {
      return restaurants;
    }

    // Fetch only when Add/Edit Catalogue needs restaurants.
    const rows = await getRestaurants("");

    const restaurantRows = Array.isArray(rows) ? rows : [];

    setRestaurants(restaurantRows);

    return restaurantRows;
  };

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const openAdd = async () => {
    const rows = await ensureRestaurants();
    const restaurant = getSSORestaurant(rows);

    if (!restaurant) {
      alert("SSO restaurant not found.");
      return;
    }

    setEditing({ mode: "ADD" });
    setForm({
      ...EMPTY_ITEM_FORM,
      restaurant_id: String(restaurant.restaurant_id ?? restaurant.id ?? ""),
    });
  };

  const openEdit = async (catalogue) => {
    const rows = await ensureRestaurants();
    const restaurant = getFixedRestaurant(rows);

    if (!restaurant) {
      alert("SSO restaurant not found.");
      return;
    }

    const restaurantId = String(
      restaurant.restaurant_id ?? restaurant.id ?? "",
    );

    if (String(catalogue?.restaurant_id ?? "") !== restaurantId) {
      alert("This catalogue belongs to another restaurant.");
      return;
    }

    setEditing({ mode: "EDIT" });
    setForm({
      catalogue_id: String(catalogue?.catalogue_id ?? catalogue?.id ?? ""),
      restaurant_id: restaurantId,
      catalogue_name: String(catalogue?.catalogue_name ?? ""),
      description: String(catalogue?.description ?? ""),
      valid_from: String(catalogue?.valid_from ?? ""),
      valid_to: String(catalogue?.valid_to ?? ""),
      status: String(catalogue?.status ?? "ACTIVE"),
    });
  };

  const closeModal = () => {
    if (catalogueSaving) return;
    setEditing(null);
    setForm(EMPTY_CATALOGUE_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success =
      editing?.mode === "EDIT"
        ? await updateCatalogue(form)
        : await insertCatalogue(form);
    if (success) {
      closeModal();
      await loadCatalogues();
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.catalogue_id ?? deleteTarget?.id;
    if (!id) return;
    const success = await deleteCatalogue(id);
    if (success) {
      setDeleteTarget(null);
      await loadCatalogues();
    }
  };

  const restaurantName = (restaurantId) => {
    const row = restaurants.find(
      (item) =>
        String(item?.restaurant_id ?? item?.id ?? "") ===
        String(restaurantId ?? ""),
    );
    return row?.restaurant_name || `Restaurant #${restaurantId || "—"}`;
  };

  return (
    <div className="space-y-3 pt-2">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Bulk Catalogues
          </h3>
          <p className="text-[11px] text-slate-500">
            Manage catalogues for the restaurants above.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadCatalogues}
            disabled={cataloguesLoading}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw
              size={13}
              className={cataloguesLoading ? "animate-spin" : ""}
            />{" "}
            Refresh
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-xs font-semibold text-white hover:bg-blue-700"
          >
            <Plus size={13} /> Add Catalogue
          </button>
        </div>
      </div>

      {cataloguesLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-xl border border-slate-200 bg-white"
            />
          ))}
        </div>
      ) : catalogues.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center">
          <p className="text-sm font-semibold text-slate-800">
            No catalogues found
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Add a catalogue and map it to a restaurant.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {catalogues.map((catalogue, index) => {
            const id = catalogue?.catalogue_id ?? catalogue?.id ?? index;
            const active =
              String(catalogue?.status || "").toUpperCase() === "ACTIVE";
            return (
              <div
                key={id}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {catalogue?.catalogue_name || "Unnamed catalogue"}
                    </p>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
                    >
                      {catalogue?.status || "-"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    ID #{id} · {restaurantName(catalogue?.restaurant_id)} ·{" "}
                    {catalogue?.valid_from || "—"} to{" "}
                    {catalogue?.valid_to || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(catalogue)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-blue-50 hover:text-blue-600"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(catalogue)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 hover:bg-rose-100"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing ? (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {editing.mode === "EDIT" ? "Edit Catalogue" : "Add Catalogue"}
                </h3>
                <p className="text-xs text-slate-500">Bulk Catalogue details</p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={catalogueSaving}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Restaurant
                  </span>

                  <input
                    type="text"
                    value={ssoUsername}
                    readOnly
                    className="h-10 w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none"
                  />
                </label>

                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Status
                  </span>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    disabled={catalogueSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Catalogue Name
                  </span>
                  <input
                    name="catalogue_name"
                    value={form.catalogue_name}
                    onChange={handleChange}
                    disabled={catalogueSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Valid From
                  </span>
                  <input
                    name="valid_from"
                    type="date"
                    value={form.valid_from}
                    onChange={handleChange}
                    disabled={catalogueSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Valid To
                  </span>
                  <input
                    name="valid_to"
                    type="date"
                    value={form.valid_to}
                    onChange={handleChange}
                    disabled={catalogueSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Description
                  </span>
                  <textarea
                    name="description"
                    rows={3}
                    value={form.description}
                    onChange={handleChange}
                    disabled={catalogueSaving}
                    className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                  />
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={catalogueSaving}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={catalogueSaving}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-blue-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {catalogueSaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : editing.mode === "EDIT" ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}
                  {catalogueSaving
                    ? "Saving..."
                    : editing.mode === "EDIT"
                      ? "Update Catalogue"
                      : "Add Catalogue"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">
              Delete Catalogue
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Delete <b>{deleteTarget?.catalogue_name || "this catalogue"}</b>?
              This action cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={Boolean(catalogueDeletingId)}
                className="h-9 rounded-lg border border-slate-200 px-4 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={Boolean(catalogueDeletingId)}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-rose-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              >
                {catalogueDeletingId ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}{" "}
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const getSSORestaurant = (rows) => {
  const username = localStorage.getItem("anlook_sso_username") || "";

  const normalize = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/marketting/g, "marketing");

  if (!username) return null;

  return (
    (Array.isArray(rows) ? rows : []).find(
      (r) => normalize(r?.restaurant_name) === normalize(username),
    ) ?? null
  );
};

const EMPTY_ITEM_CATEGORY_FORM = {
  category_id: "",
  restaurant_id: "",
  parent_category_id: "",
  category_name: "",
  description: "",
  display_order: "",
  status: "ACTIVE",
};

function BulkItemCategories() {
  const {
    getRestaurants,
    getItemCategories,
    insertItemCategory,
    updateItemCategory,
    deleteItemCategory,
    itemCategorySaving,
    itemCategoriesLoading,
    itemCategoryDeletingId,
  } = useBulkCatalogue();

  const [restaurants, setRestaurants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY_ITEM_CATEGORY_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadData = async () => {
    const [categoryRows, restaurantRows] = await Promise.all([
      getItemCategories(""),
      getRestaurants(""),
    ]);
    setCategories(Array.isArray(categoryRows) ? categoryRows : []);
    setRestaurants(Array.isArray(restaurantRows) ? restaurantRows : []);
  };

  const ensureRestaurants = async () => {
    if (restaurants.length) return restaurants;
    const rows = await getRestaurants("");
    const next = Array.isArray(rows) ? rows : [];
    setRestaurants(next);
    return next;
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const openAdd = async () => {
    const restaurantRows = await ensureRestaurants();
    setEditing({ mode: "ADD" });
    setForm({
      ...EMPTY_ITEM_CATEGORY_FORM,
      restaurant_id: String(
        restaurantRows?.[0]?.restaurant_id ?? restaurantRows?.[0]?.id ?? "",
      ),
    });
  };

  const openEdit = async (category) => {
    const rows = await ensureRestaurants();
    const restaurant = getSSORestaurant(rows);

    if (!restaurant) {
      alert("SSO restaurant not found.");
      return;
    }

    const restaurantId = String(
      restaurant.restaurant_id ?? restaurant.id ?? "",
    );

    if (String(category?.restaurant_id ?? "") !== restaurantId) {
      alert("This category belongs to another restaurant.");
      return;
    }

    setEditing({ mode: "EDIT" });
    setForm({
      category_id: String(category?.category_id ?? category?.id ?? ""),
      restaurant_id: restaurantId,
      parent_category_id: String(category?.parent_category_id ?? ""),
      category_name: String(category?.category_name ?? ""),
      description: String(category?.description ?? ""),
      display_order: String(category?.display_order ?? ""),
      status: String(category?.status ?? "ACTIVE"),
    });
  };

  const closeModal = () => {
    if (itemCategorySaving) return;
    setEditing(null);
    setForm(EMPTY_ITEM_CATEGORY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const parentCategoryId = String(form.parent_category_id ?? "").trim();

    const payload = {
      ...form,

      // Empty parent means this is a root category
      parent_category_id: parentCategoryId ? Number(parentCategoryId) : null,
    };

    const success =
      editing?.mode === "EDIT"
        ? await updateItemCategory(payload)
        : await insertItemCategory(payload);

    if (success) {
      closeModal();
      await loadData();
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.category_id ?? deleteTarget?.id;
    if (!id) return;
    const success = await deleteItemCategory(id);
    if (success) {
      setDeleteTarget(null);
      await loadData();
    }
  };

  const restaurantName = (restaurantId) => {
    const row = restaurants.find(
      (item) =>
        String(item?.restaurant_id ?? item?.id ?? "") ===
        String(restaurantId ?? ""),
    );
    return row?.restaurant_name || `Restaurant #${restaurantId || "—"}`;
  };

  const categoryName = (categoryId) => {
    if (!categoryId) return "Root category";
    const row = categories.find(
      (item) =>
        String(item?.category_id ?? item?.id ?? "") === String(categoryId),
    );
    return row?.category_name || `Category #${categoryId}`;
  };

  const parentOptions = categories.filter((item) => {
    const id = String(item?.category_id ?? item?.id ?? "");
    const sameRestaurant =
      !form.restaurant_id ||
      String(item?.restaurant_id ?? "") === String(form.restaurant_id);
    return id && id !== String(form.category_id || "") && sameRestaurant;
  });

  return (
    <div className="space-y-3 pt-2">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Item Categories
          </h3>
          <p className="text-[11px] text-slate-500">
            Manage restaurant item categories and parent categories.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadData}
            disabled={itemCategoriesLoading}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw
              size={13}
              className={itemCategoriesLoading ? "animate-spin" : ""}
            />{" "}
            Refresh
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-violet-600 px-3 text-xs font-semibold text-white hover:bg-violet-700"
          >
            <Plus size={13} /> Add Category
          </button>
        </div>
      </div>

      {itemCategoriesLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-xl border border-slate-200 bg-white"
            />
          ))}
        </div>
      ) : categories.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center">
          <p className="text-sm font-semibold text-slate-800">
            No item categories found
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Add the first category for a restaurant.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {categories.map((category, index) => {
            const id = category?.category_id ?? category?.id ?? index;
            const active =
              String(category?.status || "").toUpperCase() === "ACTIVE";
            return (
              <div
                key={id}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-semibold text-slate-900">
                      {category?.category_name || "Unnamed category"}
                    </p>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${active ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
                    >
                      {category?.status || "-"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    ID #{id} · {restaurantName(category?.restaurant_id)} ·
                    Parent: {categoryName(category?.parent_category_id)} ·
                    Order: {category?.display_order || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(category)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-violet-50 hover:text-violet-600"
                  title="Edit"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(category)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 hover:bg-rose-100"
                  title="Delete"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing ? (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {editing.mode === "EDIT"
                    ? "Edit Item Category"
                    : "Add Item Category"}
                </h3>
                <p className="text-xs text-slate-500">
                  Bulk Catalogue category details
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={itemCategorySaving}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Restaurant
                  </span>

                  <input
                    type="text"
                    value={localStorage.getItem("anlook_sso_username") || ""}
                    readOnly
                    className="h-10 w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-700 outline-none"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Parent Category
                  </span>
                  <select
                    name="parent_category_id"
                    value={form.parent_category_id}
                    onChange={handleChange}
                    disabled={itemCategorySaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="">No Parent / Root</option>
                    {parentOptions.map((category) => {
                      const id = category?.category_id ?? category?.id;
                      return (
                        <option key={id} value={id}>
                          {category?.category_name || `Category #${id}`}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Category Name
                  </span>
                  <input
                    name="category_name"
                    value={form.category_name}
                    onChange={handleChange}
                    disabled={itemCategorySaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Display Order
                  </span>
                  <input
                    name="display_order"
                    type="number"
                    value={form.display_order}
                    onChange={handleChange}
                    disabled={itemCategorySaving}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Status
                  </span>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    disabled={itemCategorySaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Description
                  </span>
                  <textarea
                    name="description"
                    rows={3}
                    value={form.description}
                    onChange={handleChange}
                    disabled={itemCategorySaving}
                    className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100"
                  />
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={itemCategorySaving}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={itemCategorySaving}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-violet-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {itemCategorySaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : editing.mode === "EDIT" ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}
                  {itemCategorySaving
                    ? "Saving..."
                    : editing.mode === "EDIT"
                      ? "Update Category"
                      : "Add Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">
              Delete Item Category
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Delete <b>{deleteTarget?.category_name || "this category"}</b>?
              This action cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={Boolean(itemCategoryDeletingId)}
                className="h-9 rounded-lg border border-slate-200 px-4 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={Boolean(itemCategoryDeletingId)}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-rose-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              >
                {itemCategoryDeletingId ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}{" "}
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const EMPTY_ITEM_FORM = {
  item_id: "",
  restaurant_id: "",
  item_name: "",
  item_description: "",
  item_type: "",
  image_url: "",
  image_data: "",
  tax_percentage: "",
  addon_price: "",
  less_price: "",
  upgrade_price: "",
  preparation_time_minutes: "",
  status: "ACTIVE",
};

function BulkItemManagement() {
  return (
    <div className="pt-1">
      <BulkItems />
    </div>
  );
}

function BulkItems() {
  const {
    getRestaurants,
    getItems,
    getItemCategories,
    getItemCategoryMappings,
    getItemVariants,
    getItemVariantPrices,
    getItemPriceTiers,
    getCatalogues,
    insertItemVariant,
    updateItemVariant,
    deleteItemVariant,
    insertItemVariantPrice,
    updateItemVariantPrice,
    deleteItemVariantPrice,
    insertItemPriceTier,
    updateItemPriceTier,
    deleteItemPriceTier,
    itemVariantSaving,
    itemVariantPriceSaving,
    itemPriceTierSaving,
    insertItem,
    updateItem,
    deleteItem,
    itemSaving,
    itemsLoading,
    itemDeletingId,
    itemImageUploading,
  } = useBulkCatalogue();

  const [restaurants, setRestaurants] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_ITEM_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedItemId, setExpandedItemId] = useState("");
  const [expandedVariantId, setExpandedVariantId] = useState("");
  const [expandedPriceId, setExpandedPriceId] = useState("");
  const [variantsByItem, setVariantsByItem] = useState({});
  const [pricesByVariant, setPricesByVariant] = useState({});
  const [tiersByPrice, setTiersByPrice] = useState({});
  const [categoriesByItem, setCategoriesByItem] = useState({});
  const [loadingNode, setLoadingNode] = useState("");
  const [nestedEditor, setNestedEditor] = useState(null);
  const [nestedForm, setNestedForm] = useState({});
  const [catalogues, setCatalogues] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [itemStatusFilter, setItemStatusFilter] = useState("ALL");

  const filteredItems = items.filter((item) => {
    const isActive = ["ACTIVE", "Y", "1"].includes(
      String(item?.status ?? "").toUpperCase(),
    );
    if (itemStatusFilter === "ACTIVE" && !isActive) return false;
    if (itemStatusFilter === "INACTIVE" && isActive) return false;
    const q = itemSearch.trim().toLowerCase();
    return (
      !q ||
      [item?.item_name, item?.item_id, item?.item_type, item?.sku].some(
        (part) =>
          String(part ?? "")
            .toLowerCase()
            .includes(q),
      )
    );
  });
  const activeItemCount = items.filter((item) =>
    ["ACTIVE", "Y", "1"].includes(String(item?.status ?? "").toUpperCase()),
  ).length;

  const openVariantEditor = (itemId, row = null) => {
    setNestedEditor({
      type: "variant",
      mode: row ? "EDIT" : "ADD",
      parentId: String(itemId),
    });
    setNestedForm(
      row
        ? { ...row }
        : {
            item_id: String(itemId),
            variant_name: "",
            unit_type: "",
            unit_value: "",
            serves_people: "",
            sku: "",
            status: "ACTIVE",
            fk_package_id: "",
          },
    );
  };

  const openPriceEditor = async (variantId, row = null) => {
    if (!catalogues.length) {
      const rows = await getCatalogues("");
      setCatalogues(Array.isArray(rows) ? rows : []);
    }
    setNestedEditor({
      type: "price",
      mode: row ? "EDIT" : "ADD",
      parentId: String(variantId),
    });
    setNestedForm(
      row
        ? {
            ...row,
            order_type:
              String(row?.order_type || "").toLowerCase() === "home_delivery"
                ? "DELIVERY"
                : String(row?.order_type || "").toUpperCase(),
            has_minimum_order: ["1", "Y", "YES", "TRUE"].includes(
              String(row?.has_minimum_order ?? "")
                .trim()
                .toUpperCase(),
            )
              ? "1"
              : "0",
          }
        : {
            variant_id: String(variantId),
            catalogue_id: "",
            order_type: "DELIVERY",
            price: "",
            has_minimum_order: "0",
            minimum_quantity: "",
            minimum_order_unit: "",
            maximum_quantity: "",
            quantity_step: "",
            valid_from: "",
            valid_to: "",
            status: "ACTIVE",
          },
    );
  };

  const openTierEditor = (variantPriceId, row = null) => {
    setNestedEditor({
      type: "tier",
      mode: row ? "EDIT" : "ADD",
      parentId: String(variantPriceId),
    });
    setNestedForm(
      row
        ? {
            ...row,
            price_type:
              row?.price_type === "FIXED"
                ? "FIXED_TOTAL"
                : row?.price_type || "",
          }
        : {
            variant_price_id: String(variantPriceId),
            minimum_quantity: "",
            maximum_quantity: "",
            price_type: "PER_UNIT",
            price: "",
          },
    );
  };

  const refreshVariants = async (itemId) => {
    const rows = await getItemVariants(itemId);
    setVariantsByItem((prev) => ({
      ...prev,
      [String(itemId)]: Array.isArray(rows) ? rows : [],
    }));
  };
  const refreshPrices = async (variantId) => {
    const rows = await getItemVariantPrices(variantId);
    setPricesByVariant((prev) => ({
      ...prev,
      [String(variantId)]: Array.isArray(rows) ? rows : [],
    }));
  };
  const refreshTiers = async (priceId) => {
    const rows = await getItemPriceTiers(priceId);
    setTiersByPrice((prev) => ({
      ...prev,
      [String(priceId)]: Array.isArray(rows) ? rows : [],
    }));
  };

  const submitNested = async (e) => {
    e.preventDefault();

    if (!nestedEditor) return;

    let ok = false;

    if (nestedEditor.type === "variant") {
      const variantPayload = {
        ...nestedForm,
        fk_package_id:
          nestedForm.fk_package_id == null ||
          String(nestedForm.fk_package_id).trim() === ""
            ? null
            : Number(nestedForm.fk_package_id),
      };

      ok =
        nestedEditor.mode === "EDIT"
          ? await updateItemVariant(variantPayload)
          : await insertItemVariant(variantPayload);

      if (ok) await refreshVariants(nestedEditor.parentId);
    } else if (nestedEditor.type === "price") {
      const pricePayload = {
        ...nestedForm,
        has_minimum_order: ["1", "Y", "YES", "TRUE"].includes(
          String(nestedForm.has_minimum_order ?? "")
            .trim()
            .toUpperCase(),
        )
          ? "1"
          : "0",
      };

      ok =
        nestedEditor.mode === "EDIT"
          ? await updateItemVariantPrice(pricePayload)
          : await insertItemVariantPrice(pricePayload);

      if (ok) await refreshPrices(nestedEditor.parentId);
    } else if (nestedEditor.type === "tier") {
      ok =
        nestedEditor.mode === "EDIT"
          ? await updateItemPriceTier(nestedForm)
          : await insertItemPriceTier(nestedForm);

      if (ok) await refreshTiers(nestedEditor.parentId);
    }

    if (ok) {
      setNestedEditor(null);
      setNestedForm({});
    }
  };

  const removeVariant = async (itemId, variantId) => {
    if (window.confirm("Delete this variant?")) {
      const ok = await deleteItemVariant(variantId);
      if (ok) await refreshVariants(itemId);
    }
  };
  const removePrice = async (variantId, priceId) => {
    if (window.confirm("Delete this variant price?")) {
      const ok = await deleteItemVariantPrice(priceId);
      if (ok) await refreshPrices(variantId);
    }
  };
  const removeTier = async (priceId, tierId) => {
    if (window.confirm("Delete this price tier?")) {
      const ok = await deleteItemPriceTier(tierId);
      if (ok) await refreshTiers(priceId);
    }
  };

  const loadData = async () => {
    // Initial tab load intentionally calls ONLY itemsGet.
    const itemRows = await getItems("");
    setItems(Array.isArray(itemRows) ? itemRows : []);
    setExpandedItemId("");
    setExpandedVariantId("");
    setExpandedPriceId("");
    setVariantsByItem({});
    setPricesByVariant({});
    setTiersByPrice({});
    setCategoriesByItem({});
  };

  const toggleItem = async (itemId) => {
    const id = String(itemId ?? "");
    if (expandedItemId === id) {
      setExpandedItemId("");
      return;
    }
    setExpandedItemId(id);
    if (Object.prototype.hasOwnProperty.call(variantsByItem, id)) return;
    setLoadingNode(`item:${id}`);
    try {
      const rows = await getItemVariants(id);
      setVariantsByItem((prev) => ({
        ...prev,
        [id]: Array.isArray(rows) ? rows : [],
      }));
    } finally {
      setLoadingNode("");
    }
  };

  const loadCategoriesForItem = async (itemId) => {
    const id = String(itemId ?? "");
    if (Object.prototype.hasOwnProperty.call(categoriesByItem, id)) return;
    setLoadingNode(`categories:${id}`);
    try {
      const [mappings, categories] = await Promise.all([
        getItemCategoryMappings(id, ""),
        getItemCategories(""),
      ]);
      const categoryRows = Array.isArray(categories) ? categories : [];
      const names = (Array.isArray(mappings) ? mappings : []).map((m) => {
        const categoryId = String(m?.category_id ?? "");
        const category = categoryRows.find(
          (c) => String(c?.category_id ?? c?.id ?? "") === categoryId,
        );
        return {
          ...m,
          category_name:
            category?.category_name || `Category #${categoryId || "—"}`,
        };
      });
      setCategoriesByItem((prev) => ({ ...prev, [id]: names }));
    } finally {
      setLoadingNode("");
    }
  };

  const toggleVariant = async (variantId) => {
    const id = String(variantId ?? "");
    if (expandedVariantId === id) {
      setExpandedVariantId("");
      return;
    }
    setExpandedVariantId(id);
    if (Object.prototype.hasOwnProperty.call(pricesByVariant, id)) return;
    setLoadingNode(`variant:${id}`);
    try {
      const rows = await getItemVariantPrices(id);
      setPricesByVariant((prev) => ({
        ...prev,
        [id]: Array.isArray(rows) ? rows : [],
      }));
    } finally {
      setLoadingNode("");
    }
  };

  const togglePrice = async (variantPriceId) => {
    const id = String(variantPriceId ?? "");
    if (expandedPriceId === id) {
      setExpandedPriceId("");
      return;
    }
    setExpandedPriceId(id);
    if (Object.prototype.hasOwnProperty.call(tiersByPrice, id)) return;
    setLoadingNode(`price:${id}`);
    try {
      const rows = await getItemPriceTiers(id);
      setTiersByPrice((prev) => ({
        ...prev,
        [id]: Array.isArray(rows) ? rows : [],
      }));
    } finally {
      setLoadingNode("");
    }
  };

  const ensureRestaurants = async () => {
    if (restaurants.length) return restaurants;
    const rows = await getRestaurants("");
    const next = Array.isArray(rows) ? rows : [];
    setRestaurants(next);
    return next;
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm((prev) => ({ ...prev, image_data: String(reader.result || "") }));
    };
    reader.readAsDataURL(file);
  };

  const openAdd = async () => {
    const restaurantRows = await ensureRestaurants();
    setEditing({ mode: "ADD" });
    setForm({
      ...EMPTY_ITEM_FORM,
      restaurant_id: String(
        restaurantRows?.[0]?.restaurant_id ?? restaurantRows?.[0]?.id ?? "",
      ),
    });
  };

  const openEdit = async (item) => {
    const rows = await ensureRestaurants();
    const restaurant = getSSORestaurant(rows);

    if (!restaurant) {
      alert("SSO restaurant not found.");
      return;
    }

    const restaurantId = String(
      restaurant.restaurant_id ?? restaurant.id ?? "",
    );

    if (String(item?.restaurant_id ?? "") !== restaurantId) {
      alert("This item belongs to another restaurant.");
      return;
    }

    setEditing({ mode: "EDIT" });
    setForm({
      item_id: String(item?.item_id ?? item?.id ?? ""),
      restaurant_id: restaurantId,
      item_name: String(item?.item_name ?? ""),
      item_description: String(item?.item_description ?? ""),
      item_type: String(item?.item_type ?? ""),
      image_url: String(item?.image_url ?? ""),
      image_data: "",
      tax_percentage: String(item?.tax_percentage ?? ""),
      addon_price: String(item?.addon_price ?? ""),
      less_price: String(item?.less_price ?? ""),
      upgrade_price: String(item?.upgrade_price ?? ""),
      preparation_time_minutes: String(item?.preparation_time_minutes ?? ""),
      status: String(item?.status ?? "ACTIVE"),
    });
  };

  const closeModal = () => {
    if (!itemSaving && !itemImageUploading) {
      setEditing(null);
      setForm(EMPTY_ITEM_FORM);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success =
      editing?.mode === "EDIT"
        ? await updateItem(form)
        : await insertItem(form);
    if (success) {
      closeModal();
      await loadData();
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget?.item_id ?? deleteTarget?.id;
    if (!id) return;
    const success = await deleteItem(id);
    if (success) {
      setDeleteTarget(null);
      await loadData();
    }
  };

  const restaurantName = (restaurantId) => {
    const row = restaurants.find(
      (r) =>
        String(r?.restaurant_id ?? r?.id ?? "") === String(restaurantId ?? ""),
    );
    return row?.restaurant_name || `Restaurant #${restaurantId || "—"}`;
  };

  const imagePreview = form.image_data || form.image_url;
  const busy = itemSaving || itemImageUploading;

  return (
    <section className="min-w-0 space-y-4 pb-4">
      {/* Workspace heading: clear purpose, search and primary action. */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 px-4 pb-4 pt-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
              <Layers3 size={20} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700">
                Catalogue workspace
              </p>
              <h2 className="mt-0.5 text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                Items & Pricing
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                Manage your items, their variants, selling prices and quantity
                tiers.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={loadData}
              disabled={itemsLoading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw
                size={14}
                className={itemsLoading ? "animate-spin" : ""}
              />
              Refresh
            </button>
            <button
              type="button"
              onClick={openAdd}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700"
            >
              <Plus size={16} /> Add Item
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 border-t border-slate-100 bg-slate-50/60 px-4 py-3 sm:px-6">
          <span className="flex items-center gap-2 text-xs text-slate-600">
            <span className="h-2 w-2 rounded-full bg-slate-400" />
            <strong className="text-slate-900">{items.length}</strong> total
            items
          </span>
          <span className="flex items-center gap-2 text-xs text-slate-600">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <strong className="text-slate-900">{activeItemCount}</strong> active
          </span>
          <span className="flex items-center gap-2 text-xs text-slate-600">
            <span className="h-2 w-2 rounded-full bg-slate-300" />
            <strong className="text-slate-900">
              {items.length - activeItemCount}
            </strong>{" "}
            inactive
          </span>
        </div>
        <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-sm">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="search"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder="Search item name, ID or type..."
              aria-label="Search catalogue items"
              className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50/60 pl-10 pr-3 text-sm text-slate-800 outline-none transition focus:border-emerald-400 focus:bg-white focus:ring-2 focus:ring-emerald-100"
            />
          </div>
          <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
            {[
              { id: "ALL", label: "All" },
              { id: "ACTIVE", label: "Active" },
              { id: "INACTIVE", label: "Inactive" },
            ].map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setItemStatusFilter(option.id)}
                aria-pressed={itemStatusFilter === option.id}
                className={`h-8 flex-1 rounded-lg px-3 text-[11px] font-semibold transition sm:flex-none ${
                  itemStatusFilter === option.id
                    ? "bg-white text-emerald-700 shadow-sm ring-1 ring-slate-200"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {itemsLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="h-28 animate-pulse rounded-2xl border border-slate-200 bg-white"
            />
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-5 py-14 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
            <Search size={22} />
          </div>
          <p className="mt-3 text-sm font-semibold text-slate-800">
            {items.length ? "No matching items" : "No catalogue items yet"}
          </p>
          <p className="mx-auto mt-1 max-w-sm text-xs leading-5 text-slate-500">
            {items.length
              ? "Try a different name or status filter."
              : "Create your first item to start setting up variants and pricing."}
          </p>
          {items.length ? (
            <button
              type="button"
              onClick={() => {
                setItemSearch("");
                setItemStatusFilter("ALL");
              }}
              className="mt-4 rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Clear filters
            </button>
          ) : (
            <button
              type="button"
              onClick={openAdd}
              className="mt-4 rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
            >
              <Plus size={13} className="mr-1 inline" /> Add your first item
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((item, index) => {
            const id = String(item?.item_id ?? item?.id ?? index);
            const active = ["ACTIVE", "Y", "1"].includes(
              String(item?.status ?? "").toUpperCase(),
            );
            const expanded = expandedItemId === id;
            const variants = variantsByItem[id] || [];
            return (
              <article
                key={id}
                className={`min-w-0 overflow-hidden rounded-2xl border bg-white shadow-sm transition-shadow hover:shadow-md ${
                  expanded
                    ? "border-emerald-200 ring-1 ring-emerald-50"
                    : "border-slate-200"
                }`}
              >
                <div className="flex flex-wrap items-center gap-3 px-4 py-3.5 sm:flex-nowrap sm:px-5">
                  <button
                    type="button"
                    onClick={() => toggleItem(id)}
                    aria-expanded={expanded}
                    aria-label={`${expanded ? "Collapse" : "Expand"} ${item?.item_name || "item"} variants`}
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition ${
                      expanded
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-slate-200 bg-white text-slate-500 hover:border-emerald-200 hover:bg-emerald-50"
                    }`}
                  >
                    {loadingNode === `item:${id}` ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : expanded ? (
                      <ChevronDown size={17} />
                    ) : (
                      <ChevronRight size={17} />
                    )}
                  </button>
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
                    {item?.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item?.item_name || "Item image"}
                        loading="lazy"
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <ImageIcon size={20} className="text-slate-400" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleItem(id)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate text-sm font-bold text-slate-900 sm:text-[15px]">
                        {item?.item_name || "Unnamed item"}
                      </h3>
                      <span
                        className={`rounded-md px-2 py-0.5 text-[10px] font-semibold ${
                          active
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
                      <span className="font-medium text-slate-600">#{id}</span>
                      <span className="inline-flex items-center gap-1">
                        <Tag size={11} />
                        {item?.item_type || "No type"}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock3 size={11} />
                        {item?.preparation_time_minutes || "—"} min prep
                      </span>
                      {Object.prototype.hasOwnProperty.call(
                        variantsByItem,
                        id,
                      ) && (
                        <span className="font-semibold text-emerald-700">
                          {variants.length} variants
                        </span>
                      )}
                    </div>
                  </button>
                  <div className="ml-auto flex shrink-0 items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => openEdit(item)}
                      aria-label={`Edit ${item?.item_name || "item"}`}
                      title="Edit item"
                      className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 text-xs font-semibold text-slate-600 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                    >
                      <Edit3 size={13} />
                      <span className="hidden sm:inline">Edit</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(item)}
                      aria-label={`Delete ${item?.item_name || "item"}`}
                      title="Delete item"
                      className="flex h-9 w-9 items-center justify-center rounded-lg border border-rose-100 text-rose-500 transition hover:bg-rose-50"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {expanded && (
                  <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-4 sm:px-5 sm:py-5">
                    <div className="mb-4 flex flex-wrap items-center gap-2 text-[10px] font-semibold text-slate-500">
                      <span className="rounded-md bg-emerald-100 px-2.5 py-1 text-emerald-800">
                        01 · Variants
                      </span>
                      <ChevronRight size={12} className="text-slate-300" />
                      <span className="rounded-md bg-white px-2.5 py-1 ring-1 ring-slate-200">
                        02 · Channel prices
                      </span>
                      <ChevronRight size={12} className="text-slate-300" />
                      <span className="rounded-md bg-white px-2.5 py-1 ring-1 ring-slate-200">
                        03 · Price tiers
                      </span>
                    </div>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-slate-900">
                          Variants
                          <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] text-slate-600">
                            {variants.length}
                          </span>
                        </p>
                        <p className="mt-1 text-[11px] text-slate-500">
                          Open a variant to manage its prices.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => loadCategoriesForItem(id)}
                          disabled={loadingNode === `categories:${id}`}
                          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                        >
                          {loadingNode === `categories:${id}` && (
                            <Loader2 size={12} className="animate-spin" />
                          )}
                          {loadingNode === `categories:${id}`
                            ? "Loading..."
                            : categoriesByItem[id]
                              ? `Categories (${categoriesByItem[id].length})`
                              : "View categories"}
                        </button>
                        <button
                          type="button"
                          onClick={() => openVariantEditor(id)}
                          className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white transition hover:bg-emerald-700"
                        >
                          <Plus size={14} /> Add Variant
                        </button>
                      </div>
                    </div>
                    {categoriesByItem[id] && (
                      <div className="mb-3 flex flex-wrap items-center gap-1.5 rounded-xl border border-violet-100 bg-white px-3 py-2.5">
                        <Tag size={13} className="mr-1 text-violet-500" />
                        {categoriesByItem[id].length ? (
                          categoriesByItem[id].map((mapping, categoryIndex) => (
                            <span
                              key={`${mapping?.category_id}-${categoryIndex}`}
                              className="rounded-full bg-violet-50 px-2.5 py-1 text-[11px] font-medium text-violet-700"
                            >
                              {mapping.category_name}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-400">
                            No category mappings
                          </span>
                        )}
                      </div>
                    )}

                    {loadingNode === `item:${id}` ? (
                      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-5 text-xs text-slate-500">
                        <Loader2 size={15} className="animate-spin" /> Loading
                        variants...
                      </div>
                    ) : variants.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center">
                        <Layers3 size={22} className="mx-auto text-slate-300" />
                        <p className="mt-2 text-xs font-semibold text-slate-700">
                          No variants for this item
                        </p>
                        <p className="mt-1 text-[11px] text-slate-500">
                          Add a size, portion or serving option to continue.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {variants.map((variant) => {
                          const variantId = String(
                            variant?.variant_id ?? variant?.id ?? "",
                          );
                          const variantExpanded =
                            expandedVariantId === variantId;
                          const prices = pricesByVariant[variantId] || [];
                          return (
                            <div
                              key={variantId}
                              className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                            >
                              <div className="flex flex-wrap items-center gap-2 p-3 sm:flex-nowrap sm:px-4">
                                <button
                                  type="button"
                                  onClick={() => toggleVariant(variantId)}
                                  aria-expanded={variantExpanded}
                                  aria-label={`${variantExpanded ? "Collapse" : "Expand"} ${variant?.variant_name || "variant"} prices`}
                                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition ${
                                    variantExpanded
                                      ? "bg-blue-50 text-blue-700"
                                      : "bg-slate-100 text-slate-500 hover:bg-blue-50"
                                  }`}
                                >
                                  {loadingNode === `variant:${variantId}` ? (
                                    <Loader2
                                      size={15}
                                      className="animate-spin"
                                    />
                                  ) : variantExpanded ? (
                                    <ChevronDown size={15} />
                                  ) : (
                                    <ChevronRight size={15} />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => toggleVariant(variantId)}
                                  className="min-w-0 flex-1 text-left"
                                >
                                  <p className="truncate text-sm font-semibold text-slate-900">
                                    {variant?.variant_name ||
                                      `Variant #${variantId}`}
                                  </p>
                                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                                    <span>#{variantId}</span>
                                    <span className="rounded-md bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                                      {variant?.unit_value || "—"}{" "}
                                      {variant?.unit_type || ""}
                                    </span>
                                    <span>
                                      Serves {variant?.serves_people || "—"}
                                    </span>
                                    {variant?.sku ? (
                                      <span>SKU {variant.sku}</span>
                                    ) : null}
                                  </div>
                                </button>
                                <div className="ml-auto flex shrink-0 items-center gap-1.5">
                                  {Object.prototype.hasOwnProperty.call(
                                    pricesByVariant,
                                    variantId,
                                  ) && (
                                    <span className="mr-1 hidden rounded-lg bg-blue-50 px-2 py-1 text-[10px] font-semibold text-blue-700 sm:inline">
                                      {prices.length} prices
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      openVariantEditor(id, variant)
                                    }
                                    title="Edit variant"
                                    aria-label="Edit variant"
                                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
                                  >
                                    <Edit3 size={13} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeVariant(id, variantId)}
                                    title="Delete variant"
                                    aria-label="Delete variant"
                                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 text-rose-500 hover:bg-rose-50"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </div>
                              {variantExpanded && (
                                <div className="border-t border-slate-100 bg-slate-50/50 p-3 sm:p-4">
                                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                      <h4 className="text-xs font-bold text-slate-800">
                                        Channel pricing
                                      </h4>
                                      <p className="mt-0.5 text-[11px] text-slate-500">
                                        Prices for each order type
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => openPriceEditor(variantId)}
                                      className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-[11px] font-semibold text-white hover:bg-blue-700"
                                    >
                                      <Plus size={13} /> Add Price
                                    </button>
                                  </div>
                                  {loadingNode === `variant:${variantId}` ? (
                                    <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-500">
                                      <Loader2
                                        size={14}
                                        className="animate-spin"
                                      />{" "}
                                      Loading prices...
                                    </div>
                                  ) : prices.length === 0 ? (
                                    <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-xs text-slate-500">
                                      No prices added. Choose Add Price to
                                      create one.
                                    </div>
                                  ) : (
                                    <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                                      {prices.map((price) => {
                                        const priceId = String(
                                          price?.variant_price_id ??
                                            price?.id ??
                                            "",
                                        );
                                        const priceExpanded =
                                          expandedPriceId === priceId;
                                        const tiers =
                                          tiersByPrice[priceId] || [];
                                        return (
                                          <div
                                            key={priceId}
                                            className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                                          >
                                            <div className="flex items-start gap-2 p-3">
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  togglePrice(priceId)
                                                }
                                                aria-expanded={priceExpanded}
                                                aria-label={`${priceExpanded ? "Collapse" : "Expand"} price ${priceId} tiers`}
                                                className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${priceExpanded ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-500 hover:bg-violet-50"}`}
                                              >
                                                {loadingNode ===
                                                `price:${priceId}` ? (
                                                  <Loader2
                                                    size={14}
                                                    className="animate-spin"
                                                  />
                                                ) : priceExpanded ? (
                                                  <ChevronDown size={15} />
                                                ) : (
                                                  <ChevronRight size={15} />
                                                )}
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  togglePrice(priceId)
                                                }
                                                className="min-w-0 flex-1 text-left"
                                              >
                                                <p className="truncate text-[10px] font-bold uppercase tracking-wide text-blue-600">
                                                  {price?.order_type ||
                                                    "Unspecified channel"}
                                                </p>
                                                <p className="mt-1 text-lg font-bold tabular-nums tracking-tight text-slate-900">
                                                  ₹{price?.price ?? "—"}
                                                </p>
                                                <p className="mt-1 text-[10px] text-slate-400">
                                                  Price #{priceId}
                                                </p>
                                              </button>
                                              <div className="flex shrink-0 items-center gap-1">
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    openPriceEditor(
                                                      variantId,
                                                      price,
                                                    )
                                                  }
                                                  title="Edit price"
                                                  aria-label="Edit price"
                                                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"
                                                >
                                                  <Edit3 size={12} />
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    removePrice(
                                                      variantId,
                                                      priceId,
                                                    )
                                                  }
                                                  title="Delete price"
                                                  aria-label="Delete price"
                                                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 text-rose-500 hover:bg-rose-50"
                                                >
                                                  <Trash2 size={12} />
                                                </button>
                                              </div>
                                            </div>
                                            {priceExpanded && (
                                              <div className="border-t border-slate-100 bg-violet-50/30 p-3">
                                                <div className="mb-2 flex items-center justify-between gap-2">
                                                  <div>
                                                    <p className="text-[11px] font-bold text-slate-700">
                                                      Quantity tiers
                                                    </p>
                                                    <p className="text-[10px] text-slate-400">
                                                      Volume-based pricing
                                                    </p>
                                                  </div>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      openTierEditor(priceId)
                                                    }
                                                    className="inline-flex h-8 items-center gap-1 rounded-lg bg-violet-600 px-2.5 text-[10px] font-semibold text-white hover:bg-violet-700"
                                                  >
                                                    <Plus size={12} /> Add Tier
                                                  </button>
                                                </div>
                                                {loadingNode ===
                                                `price:${priceId}` ? (
                                                  <p className="rounded-lg bg-white p-3 text-xs text-slate-500">
                                                    Loading tiers...
                                                  </p>
                                                ) : tiers.length === 0 ? (
                                                  <div className="rounded-lg border border-dashed border-violet-200 bg-white p-3 text-center text-[11px] text-slate-500">
                                                    No price tiers configured.
                                                  </div>
                                                ) : (
                                                  <div className="space-y-2">
                                                    {tiers.map(
                                                      (tier, tierIndex) => (
                                                        <div
                                                          key={
                                                            tier?.item_price_tier_id ??
                                                            tierIndex
                                                          }
                                                          className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5"
                                                        >
                                                          <div className="min-w-0 flex-1">
                                                            <p className="text-[11px] font-semibold text-slate-700">
                                                              Qty{" "}
                                                              {tier?.minimum_quantity ??
                                                                "—"}{" "}
                                                              –{" "}
                                                              {tier?.maximum_quantity ??
                                                                "—"}
                                                            </p>
                                                            <p className="mt-0.5 text-[10px] text-slate-400">
                                                              {tier?.price_type ||
                                                                "Tier"}
                                                            </p>
                                                          </div>
                                                          <span className="text-sm font-bold tabular-nums text-slate-900">
                                                            ₹
                                                            {tier?.price ?? "—"}
                                                          </span>
                                                          <button
                                                            type="button"
                                                            onClick={() =>
                                                              openTierEditor(
                                                                priceId,
                                                                tier,
                                                              )
                                                            }
                                                            title="Edit tier"
                                                            aria-label="Edit tier"
                                                            className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
                                                          >
                                                            <Edit3 size={12} />
                                                          </button>
                                                          <button
                                                            type="button"
                                                            onClick={() =>
                                                              removeTier(
                                                                priceId,
                                                                tier?.item_price_tier_id,
                                                              )
                                                            }
                                                            title="Delete tier"
                                                            aria-label="Delete tier"
                                                            className="flex h-7 w-7 items-center justify-center rounded-md border border-rose-100 text-rose-500 hover:bg-rose-50"
                                                          >
                                                            <Trash2 size={12} />
                                                          </button>
                                                        </div>
                                                      ),
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {editing ? (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <div className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {editing.mode === "EDIT" ? "Edit Item" : "Add Item"}
                </h3>
                <p className="text-xs text-slate-500">
                  Bulk Catalogue item details
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={busy}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Restaurant
                  </span>
                  <select
                    name="restaurant_id"
                    value={form.restaurant_id}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="">Select Restaurant</option>
                    {restaurants.map((r) => {
                      const id = r?.restaurant_id ?? r?.id;
                      return (
                        <option key={id} value={id}>
                          {r?.restaurant_name || `Restaurant #${id}`}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Status
                  </span>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Item Name
                  </span>
                  <input
                    name="item_name"
                    value={form.item_name}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Item Type
                  </span>

                  <select
                    name="item_type"
                    value={form.item_type || ""}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="">Select Item Type</option>
                    <option value="VEG">Veg</option>
                    <option value="NON_VEG">NonVeg</option>
                  </select>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Description
                  </span>
                  <textarea
                    name="item_description"
                    rows={3}
                    value={form.item_description}
                    onChange={handleChange}
                    disabled={busy}
                    className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
                  />
                </label>

                <div className="sm:col-span-2 rounded-xl border border-slate-200 p-3">
                  <span className="mb-2 block text-xs font-semibold text-slate-700">
                    Item Image
                  </span>
                  <div className="flex items-center gap-3">
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="h-20 w-20 rounded-xl border border-slate-200 object-cover"
                      />
                    ) : (
                      <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-dashed border-slate-300 text-[10px] text-slate-400">
                        No image
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={busy}
                        className="block w-full text-xs text-slate-600"
                      />
                      <p className="mt-1 text-[10px] text-slate-400">
                        On Save: PHP upload API runs first. Its image_url is
                        then sent to itemsInsert/itemsUpdate.
                      </p>
                    </div>
                  </div>
                </div>

                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Tax %
                  </span>
                  <input
                    name="tax_percentage"
                    type="number"
                    step="0.01"
                    value={form.tax_percentage}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Preparation Time (minutes)
                  </span>
                  <input
                    name="preparation_time_minutes"
                    type="number"
                    value={form.preparation_time_minutes}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Addon Price
                  </span>
                  <input
                    name="addon_price"
                    type="number"
                    step="0.01"
                    value={form.addon_price}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Less Price
                  </span>
                  <input
                    name="less_price"
                    type="number"
                    step="0.01"
                    value={form.less_price}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Upgrade Price
                  </span>
                  <input
                    name="upgrade_price"
                    type="number"
                    step="0.01"
                    value={form.upgrade_price}
                    onChange={handleChange}
                    disabled={busy}
                    className="h-10 w-full rounded-xl border border-slate-200 px-3 text-sm"
                  />
                </label>
              </div>
              <div className="sticky bottom-0 flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={busy}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-amber-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {busy ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : editing.mode === "EDIT" ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}
                  {itemImageUploading
                    ? "Uploading image..."
                    : itemSaving
                      ? "Saving..."
                      : editing.mode === "EDIT"
                        ? "Update Item"
                        : "Add Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {nestedEditor ? (
        <div className="fixed inset-0 z-[12500] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <form
            onSubmit={submitNested}
            className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {nestedEditor.mode === "EDIT" ? "Edit" : "Add"}{" "}
                  {nestedEditor.type === "variant"
                    ? "Variant"
                    : nestedEditor.type === "price"
                      ? "Variant Price"
                      : "Price Tier"}
                </h3>
                <p className="text-xs text-slate-500">
                  Parent ID #{nestedEditor.parentId}
                </p>
              </div>
              <button type="button" onClick={() => setNestedEditor(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
              {nestedEditor.type === "variant" && (
                <>
                  <label className="text-xs font-medium">
                    Variant Name
                    <input
                      required
                      value={nestedForm.variant_name || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          variant_name: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Unit Type
                    <select
                      value={nestedForm.unit_type || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          unit_type: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3"
                    >
                      <option value="">Select Unit Type</option>
                      <option value="PIECE">PIECE</option>
                      <option value="ML">ML</option>
                      <option value="GRAM">GRAM</option>
                      <option value="KG">KG</option>
                      <option value="PORTION">PORTION</option>
                      <option value="PLATE">PLATE</option>
                      <option value="BOX">BOX</option>
                      <option value="PERSON">PERSON</option>
                      <option value="TRAY">TRAY</option>
                      <option value="OTHER">OTHER</option>
                    </select>
                  </label>
                  <label className="text-xs font-medium">
                    Unit Value
                    <input
                      value={nestedForm.unit_value || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          unit_value: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Serves People
                    <input
                      value={nestedForm.serves_people || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          serves_people: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    SKU
                    <input
                      value={nestedForm.sku || ""}
                      onChange={(e) =>
                        setNestedForm({ ...nestedForm, sku: e.target.value })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Package ID
                    <input
                      value={nestedForm.fk_package_id ?? ""}
                      onChange={(e) =>
                        setNestedForm((prev) => ({
                          ...prev,
                          fk_package_id:
                            e.target.value.trim() === ""
                              ? null
                              : e.target.value,
                        }))
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                </>
              )}
              {nestedEditor.type === "price" && (
                <>
                  <label className="text-xs font-medium">
                    Catalogue
                    <select
                      required
                      value={nestedForm.catalogue_id || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          catalogue_id: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    >
                      <option value="">Select catalogue</option>
                      {catalogues.map((c) => (
                        <option key={c.catalogue_id} value={c.catalogue_id}>
                          {c.catalogue_name || `Catalogue #${c.catalogue_id}`}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs font-medium">
                    Order Type
                    <select
                      required
                      value={nestedForm.order_type || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          order_type: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3"
                    >
                      <option value="">Select Order Type</option>
                      <option value="DELIVERY">DELIVERY</option>
                      <option value="TAKEAWAY">TAKEAWAY</option>
                      <option value="DINING">DINING</option>
                      <option value="CATERING">CATERING</option>
                      <option value="DISTRIBUTION">DISTRIBUTION</option>
                      <option value="ALL">ALL</option>
                    </select>
                  </label>
                  <label className="text-xs font-medium">
                    Price
                    <input
                      required
                      type="number"
                      step="0.01"
                      value={nestedForm.price || ""}
                      onChange={(e) =>
                        setNestedForm({ ...nestedForm, price: e.target.value })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Minimum Order
                    <select
                      value={nestedForm.has_minimum_order ?? "0"}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          has_minimum_order: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3"
                    >
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </select>
                  </label>
                  <label className="text-xs font-medium">
                    Minimum Qty
                    <input
                      value={nestedForm.minimum_quantity || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          minimum_quantity: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Minimum Unit
                    <input
                      value={nestedForm.minimum_order_unit || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          minimum_order_unit: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Maximum Qty
                    <input
                      value={nestedForm.maximum_quantity || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          maximum_quantity: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Quantity Step
                    <input
                      value={nestedForm.quantity_step || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          quantity_step: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Valid From
                    <input
                      type="date"
                      value={(nestedForm.valid_from || "").slice(0, 10)}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          valid_from: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Valid To
                    <input
                      type="date"
                      value={(nestedForm.valid_to || "").slice(0, 10)}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          valid_to: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                </>
              )}
              {nestedEditor.type === "tier" && (
                <>
                  <label className="text-xs font-medium">
                    Minimum Quantity
                    <input
                      required
                      type="number"
                      value={nestedForm.minimum_quantity || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          minimum_quantity: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                  <label className="text-xs font-medium">
                    Maximum Quantity
                    <input
                      type="number"
                      value={nestedForm.maximum_quantity || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          maximum_quantity: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>

                  <label className="text-xs font-medium">
                    Price Type
                    <select
                      required
                      value={nestedForm.price_type || ""}
                      onChange={(e) =>
                        setNestedForm({
                          ...nestedForm,
                          price_type: e.target.value,
                        })
                      }
                      className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 outline-none focus:border-violet-400"
                    >
                      <option value="" disabled>
                        Select Price Type
                      </option>

                      <option value="PER_UNIT">Per Unit</option>
                      <option value="FIXED_TOTAL">Fixed Total</option>
                    </select>
                  </label>

                  <label className="text-xs font-medium">
                    Price
                    <input
                      required
                      type="number"
                      step="0.01"
                      value={nestedForm.price || ""}
                      onChange={(e) =>
                        setNestedForm({ ...nestedForm, price: e.target.value })
                      }
                      className="mt-1 h-9 w-full rounded-lg border px-3"
                    />
                  </label>
                </>
              )}
              <label className="text-xs font-medium">
                Status
                <select
                  value={nestedForm.status || "ACTIVE"}
                  onChange={(e) =>
                    setNestedForm({ ...nestedForm, status: e.target.value })
                  }
                  className="mt-1 h-9 w-full rounded-lg border px-3"
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2 border-t px-4 py-3">
              <button
                type="button"
                onClick={() => setNestedEditor(null)}
                className="h-9 rounded-lg border px-4 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  itemVariantSaving ||
                  itemVariantPriceSaving ||
                  itemPriceTierSaving
                }
                className="h-9 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              >
                {nestedEditor.mode === "EDIT" ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">
              Delete Item
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Delete <b>{deleteTarget?.item_name || "this item"}</b>? This
              action cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={Boolean(itemDeletingId)}
                className="h-9 rounded-lg border border-slate-200 px-4 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={Boolean(itemDeletingId)}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-rose-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              >
                {itemDeletingId ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}{" "}
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}


function SearchableItemDropdown({
  items = [],
  value,
  onChange,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const getId = (item) => String(item?.item_id ?? item?.id ?? "");

  const selected = items.find((item) => getId(item) === String(value));

  const filtered = items.filter((item) => {
    const query = search.trim().toLowerCase();

    return (
      String(item?.item_name ?? "")
        .toLowerCase()
        .includes(query) || getId(item).includes(query)
    );
  });

  useEffect(() => {
    if (!open) return;

    searchRef.current?.focus();

    const handleOutsideClick = (e) => {
      if (!dropdownRef.current?.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  const chooseItem = (item) => {
    onChange(getId(item));
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={dropdownRef} className="relative min-w-0">
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        Item
      </span>

      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((prev) => !prev);
          setSearch("");
        }}
        aria-expanded={open && !disabled}
        aria-haspopup="listbox"
        className={`flex h-10 w-full items-center justify-between gap-2 rounded-xl border bg-white px-3 text-left text-sm shadow-sm transition outline-none disabled:cursor-not-allowed disabled:opacity-60 ${
          open
            ? "border-emerald-400 ring-2 ring-emerald-100"
            : "border-slate-200 hover:border-slate-300"
        }`}
      >
        <span
          className={`truncate ${
            selected ? "text-slate-800" : "text-slate-400"
          }`}
        >
          {selected?.item_name || (value ? `Item #${value}` : "Select Item")}
        </span>

        <ChevronDown
          size={16}
          className={`shrink-0 text-slate-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && !disabled && (
        <div className="absolute left-0 right-0 top-full z-[1000] mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
          {/* SEARCH BAR */}
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                ref={searchRef}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (filtered.length) chooseItem(filtered[0]);
                  }

                  if (e.key === "Escape") {
                    setOpen(false);
                    setSearch("");
                  }
                }}
                placeholder="Search item..."
                className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none"
              />
            </div>
          </div>

          {/* 6 VISIBLE OPTIONS - SCROLLBAR HIDDEN */}
          <div
            role="listbox"
            className="max-h-[216px] overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {filtered.length > 0 ? (
              filtered.map((item) => {
                const id = getId(item);
                const active = String(value) === id;

                return (
                  <button
                    key={id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => chooseItem(item)}
                    className={`flex h-9 w-full items-center px-3 text-left text-sm transition ${
                      active
                        ? "bg-emerald-50 font-medium text-emerald-700"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span className="truncate">
                      {item?.item_name || `Item #${id}`}
                    </span>
                  </button>
                );
              })
            ) : (
              <p className="px-3 py-4 text-center text-sm text-slate-400">
                No items found
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


const EMPTY_MAPPING_FORM = { item_id: "", category_id: "", is_primary: "0" };

function BulkItemCategoryMappings() {
  const {
    getItems,
    getItemCategories,
    getItemCategoryMappings,
    insertItemCategoryMapping,
    updateItemCategoryMapping,
    deleteItemCategoryMapping,
    itemCategoryMappingSaving,
    itemCategoryMappingsLoading,
    itemCategoryMappingDeletingKey,
  } = useBulkCatalogue();

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [form, setForm] = useState(EMPTY_MAPPING_FORM);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  
const [mappingSearch, setMappingSearch] = useState("");

const filteredMappings = mappings.filter((row) => {
  const normalize = (value) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const query = normalize(mappingSearch.trim());

  if (!query) return true;

  const matchedItem = items.find(
    (item) =>
      String(item?.item_id ?? item?.id ?? "") === String(row?.item_id ?? ""),
  );

  const matchedCategory = categories.find(
    (category) =>
      String(category?.category_id ?? category?.id ?? "") ===
      String(row?.category_id ?? ""),
  );

  const isPrimary = ["1", "Y", "YES", "TRUE"].includes(
    String(row?.is_primary ?? "").toUpperCase(),
  );

  const searchableFields = [
    row?.item_id,
    matchedItem?.item_name,
    row?.category_id,
    matchedCategory?.category_name,
    isPrimary ? "Primary Yes True" : "Secondary No False",
  ];

  return searchableFields.some((value) => normalize(value).includes(query));
});


  const loadMappings = async () => {
    const [mappingRows, itemRows, categoryRows] = await Promise.all([
      getItemCategoryMappings("", ""),
      getItems(""),
      getItemCategories(""),
    ]);
    setMappings(Array.isArray(mappingRows) ? mappingRows : []);
    setItems(Array.isArray(itemRows) ? itemRows : []);
    setCategories(Array.isArray(categoryRows) ? categoryRows : []);
  };

  useEffect(() => {
    loadMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureMappingOptions = async () => {
    if (items.length && categories.length)
      return { itemRows: items, categoryRows: categories };
    const [itemRowsRaw, categoryRowsRaw] = await Promise.all([
      items.length ? Promise.resolve(items) : getItems(""),
      categories.length ? Promise.resolve(categories) : getItemCategories(""),
    ]);
    const itemRows = Array.isArray(itemRowsRaw) ? itemRowsRaw : [];
    const categoryRows = Array.isArray(categoryRowsRaw) ? categoryRowsRaw : [];
    setItems(itemRows);
    setCategories(categoryRows);
    return { itemRows, categoryRows };
  };

  const itemName = (id) =>
    items.find((x) => String(x?.item_id ?? x?.id ?? "") === String(id ?? ""))
      ?.item_name || `Item #${id || "—"}`;
  const categoryName = (id) =>
    categories.find(
      (x) => String(x?.category_id ?? x?.id ?? "") === String(id ?? ""),
    )?.category_name || `Category #${id || "—"}`;

  const openAdd = async () => {
    const { itemRows, categoryRows } = await ensureMappingOptions();
    setEditing({ mode: "ADD" });
    setForm({
      item_id: String(itemRows?.[0]?.item_id ?? itemRows?.[0]?.id ?? ""),
      category_id: String(
        categoryRows?.[0]?.category_id ?? categoryRows?.[0]?.id ?? "",
      ),
      is_primary: "0",
    });
  };

  const openEdit = async (row) => {
    await ensureMappingOptions();
    setEditing({ mode: "EDIT" });
    setForm({
      item_id: String(row?.item_id ?? ""),
      category_id: String(row?.category_id ?? ""),
      is_primary: String(row?.is_primary ?? "0"),
    });
  };

  const closeModal = () => {
    if (itemCategoryMappingSaving) return;
    setEditing(null);
    setForm(EMPTY_MAPPING_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ok =
      editing?.mode === "EDIT"
        ? await updateItemCategoryMapping(form)
        : await insertItemCategoryMapping(form);
    if (ok) {
      closeModal();
      await loadMappings();
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const ok = await deleteItemCategoryMapping(
      deleteTarget?.item_id,
      deleteTarget?.category_id,
    );
    if (ok) {
      setDeleteTarget(null);
      await loadMappings();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Item Category Mapping
          </h3>
          <p className="text-[11px] text-slate-500">
            Map items to categories and select the primary category.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* SEARCH BAR */}
          <div className="relative w-full sm:w-64 lg:w-80">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              value={mappingSearch}
              onChange={(e) => setMappingSearch(e.target.value)}
              placeholder="Search item, category, ID, primary..."
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-xs text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />

            {mappingSearch && (
              <button
                type="button"
                onClick={() => setMappingSearch("")}
                className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={13} />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={loadMappings}
            disabled={itemCategoryMappingsLoading}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-600 disabled:opacity-50"
          >
            <RefreshCw
              size={13}
              className={itemCategoryMappingsLoading ? "animate-spin" : ""}
            />{" "}
            Refresh
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white"
          >
            <Plus size={13} /> Add Mapping
          </button>
        </div>
      </div>

      {itemCategoryMappingsLoading ? (
        <div className="h-24 animate-pulse rounded-xl border border-slate-200 bg-white" />
      ) : filteredMappings.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-12 text-center text-sm text-slate-500">
          {mappingSearch.trim()
            ? "No mappings match your search."
            : "No item category mappings found."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {filteredMappings.map((row, index) => {
            const key = `${row?.item_id}-${row?.category_id}-${index}`;
            const primary = ["Y", "YES", "TRUE", "1"].includes(
              String(row?.is_primary ?? "").toUpperCase(),
            );
            return (
              <div
                key={key}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900">
                      {itemName(row?.item_id)}
                    </p>
                    <span className="text-xs text-slate-400">→</span>
                    <p className="text-sm font-medium text-slate-700">
                      {categoryName(row?.category_id)}
                    </p>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${primary ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                    >
                      {primary ? "PRIMARY" : "SECONDARY"}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Item #{row?.item_id || "—"} · Category #
                    {row?.category_id || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(row)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(row)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-[2px]">
          <div className="relative w-full max-w-lg overflow-visible rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {editing.mode === "EDIT" ? "Edit Mapping" : "Add Mapping"}
                </h3>
                <p className="text-xs text-slate-500">
                  Item and category relation
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                disabled={itemCategoryMappingSaving}
                className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                <SearchableItemDropdown
                  items={items}
                  value={form.item_id}
                  disabled={itemCategoryMappingSaving}
                  onChange={(id) =>
                    setForm((prev) => ({
                      ...prev,
                      item_id: id,
                    }))
                  }
                />

                <label>
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Category
                  </span>
                  <select
                    value={form.category_id}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, category_id: e.target.value }))
                    }
                    disabled={itemCategoryMappingSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    {categories.map((row) => {
                      const id = row?.category_id ?? row?.id;
                      return (
                        <option key={id} value={id}>
                          {row?.category_name || `Category #${id}`}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-700">
                    Primary Category
                  </span>
                  <select
                    value={form.is_primary}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, is_primary: e.target.value }))
                    }
                    disabled={itemCategoryMappingSaving}
                    className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  >
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={itemCategoryMappingSaving}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    itemCategoryMappingSaving ||
                    !form.item_id ||
                    !form.category_id
                  }
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {itemCategoryMappingSaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : editing.mode === "EDIT" ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}
                  {itemCategoryMappingSaving
                    ? "Saving..."
                    : editing.mode === "EDIT"
                      ? "Update Mapping"
                      : "Add Mapping"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[13000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">
              Delete Mapping
            </h3>
            <p className="mt-1 text-xs leading-5 text-slate-500">
              Remove <b>{itemName(deleteTarget?.item_id)}</b> from{" "}
              <b>{categoryName(deleteTarget?.category_id)}</b>?
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={Boolean(itemCategoryMappingDeletingKey)}
                className="h-9 rounded-lg border border-slate-200 px-4 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={Boolean(itemCategoryMappingDeletingKey)}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-rose-600 px-4 text-xs font-semibold text-white disabled:opacity-50"
              >
                {itemCategoryMappingDeletingKey ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Trash2 size={13} />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
